import React, { useState } from 'react';
import { 
  XMarkIcon, 
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import type { Tenant } from './TenantTable';

interface BillingAdjustment {
  tenantId: string;
  type: 'credit' | 'refund' | 'charge' | 'proration';
  amount: number;
  currency: string;
  reason: string;
  description: string;
  invoiceId?: string;
  metadata?: Record<string, any>;
}

interface BillingAdjustmentFormProps {
  isOpen: boolean;
  tenant: Tenant | null;
  onClose: () => void;
  onSubmit: (adjustment: BillingAdjustment) => void;
}

const BillingAdjustmentForm: React.FC<BillingAdjustmentFormProps> = ({
  isOpen,
  tenant,
  onClose,
  onSubmit
}) => {
  const [formData, setFormData] = useState<Partial<BillingAdjustment>>({
    type: 'credit',
    amount: 0,
    currency: 'USD',
    reason: '',
    description: '',
    invoiceId: '',
    metadata: {}
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const adjustmentTypes = [
    {
      value: 'credit' as const,
      label: 'Account Credit',
      description: 'Add credit to customer account',
      icon: '💳',
      color: 'text-green-600 bg-green-50 border-green-200'
    },
    {
      value: 'refund' as const,
      label: 'Refund',
      description: 'Process refund through payment gateway',
      icon: '💰',
      color: 'text-blue-600 bg-blue-50 border-blue-200'
    },
    {
      value: 'charge' as const,
      label: 'One-time Charge',
      description: 'Add one-time charge to next invoice',
      icon: '💸',
      color: 'text-purple-600 bg-purple-50 border-purple-200'
    },
    {
      value: 'proration' as const,
      label: 'Proration Adjustment',
      description: 'Adjust for plan changes or billing corrections',
      icon: '⚖️',
      color: 'text-orange-600 bg-orange-50 border-orange-200'
    }
  ];

  const reasonOptions = [
    'Service outage compensation',
    'Billing error correction',
    'Customer service gesture',
    'Plan change proration',
    'Feature unavailability',
    'Data loss incident',
    'Security incident compensation',
    'Early termination refund',
    'Duplicate charge',
    'Custom service charge',
    'Other'
  ];

  const updateFormData = (updates: Partial<BillingAdjustment>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const validateForm = (): boolean => {
    return !!(
      formData.type &&
      formData.amount &&
      formData.amount > 0 &&
      formData.reason &&
      formData.description
    );
  };

  const calculateImpact = () => {
    if (!formData.amount || !tenant?.monthly_revenue) return null;
    
    const monthlyRevenue = tenant.monthly_revenue;
    const impactPercentage = (formData.amount / monthlyRevenue) * 100;
    
    return {
      monthlyRevenue,
      impactPercentage: Math.round(impactPercentage * 100) / 100,
      nextBillingImpact: formData.type === 'credit' ? -formData.amount : formData.amount
    };
  };

  const handleSubmit = async () => {
    if (!validateForm() || !tenant) return;

    setIsLoading(true);
    try {
      const adjustment: BillingAdjustment = {
        tenantId: tenant.id,
        type: formData.type!,
        amount: formData.amount!,
        currency: formData.currency!,
        reason: formData.reason!,
        description: formData.description!,
        invoiceId: formData.invoiceId,
        metadata: {
          processedBy: 'admin',
          timestamp: new Date().toISOString(),
          tenantName: tenant.name,
          ...formData.metadata
        }
      };

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      onSubmit(adjustment);
      toast.success(`${formData.type?.charAt(0).toUpperCase()}${formData.type?.slice(1)} processed successfully`);
      onClose();
      resetForm();
    } catch (error) {
      toast.error('Failed to process billing adjustment');
    } finally {
      setIsLoading(false);
      setShowConfirmation(false);
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'credit',
      amount: 0,
      currency: 'USD',
      reason: '',
      description: '',
      invoiceId: '',
      metadata: {}
    });
    setShowConfirmation(false);
  };

  const selectedType = adjustmentTypes.find(type => type.value === formData.type);
  const impact = calculateImpact();

  if (!isOpen || !tenant) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Billing Adjustment</h3>
                  <p className="text-sm text-gray-500">{tenant.name}</p>
                </div>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {!showConfirmation ? (
            <div className="px-6 py-6 space-y-6">
              {/* Adjustment Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Adjustment Type *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {adjustmentTypes.map((type) => (
                    <div
                      key={type.value}
                      onClick={() => updateFormData({ type: type.value })}
                      className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all ${
                        formData.type === type.value
                          ? type.color
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <span className="text-2xl">{type.icon}</span>
                        <div>
                          <h4 className="font-medium text-gray-900">{type.label}</h4>
                          <p className="text-sm text-gray-600">{type.description}</p>
                        </div>
                      </div>
                      {formData.type === type.value && (
                        <CheckCircleIcon className="absolute top-2 right-2 h-5 w-5 text-current" />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Amount and Currency */}
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.amount || ''}
                    onChange={(e) => updateFormData({ amount: parseFloat(e.target.value) || 0 })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Currency
                  </label>
                  <select
                    value={formData.currency}
                    onChange={(e) => updateFormData({ currency: e.target.value })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason *
                </label>
                <select
                  value={formData.reason}
                  onChange={(e) => updateFormData({ reason: e.target.value })}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                >
                  <option value="">Select a reason</option>
                  {reasonOptions.map((reason) => (
                    <option key={reason} value={reason}>{reason}</option>
                  ))}
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description *
                </label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => updateFormData({ description: e.target.value })}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  placeholder="Provide details about this adjustment..."
                />
              </div>

              {/* Invoice ID (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Invoice ID (Optional)
                </label>
                <input
                  type="text"
                  value={formData.invoiceId || ''}
                  onChange={(e) => updateFormData({ invoiceId: e.target.value })}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  placeholder="inv_1234567890"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Link this adjustment to a specific invoice
                </p>
              </div>

              {/* Impact Preview */}
              {impact && formData.amount && formData.amount > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <InformationCircleIcon className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-medium text-blue-800">Impact Preview</h4>
                      <div className="mt-2 text-sm text-blue-700 space-y-1">
                        <p>Monthly Revenue: ${impact.monthlyRevenue.toLocaleString()}</p>
                        <p>Adjustment: ${formData.amount.toLocaleString()} ({impact.impactPercentage}% of monthly revenue)</p>
                        <p>
                          Next Billing Impact: 
                          <span className={impact.nextBillingImpact < 0 ? 'text-green-600' : 'text-red-600'}>
                            {impact.nextBillingImpact < 0 ? '-' : '+'}${Math.abs(impact.nextBillingImpact).toLocaleString()}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* High Amount Warning */}
              {impact && impact.impactPercentage > 50 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start space-x-2">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-medium text-yellow-800">High Impact Adjustment</h4>
                      <p className="mt-1 text-sm text-yellow-700">
                        This adjustment represents more than 50% of the tenant's monthly revenue. 
                        Please verify the amount and consider approval from a senior administrator.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Confirmation Screen */
            <div className="px-6 py-6">
              <div className="text-center mb-6">
                <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full ${selectedType?.color}`}>
                  <span className="text-2xl">{selectedType?.icon}</span>
                </div>
                <h3 className="mt-4 text-lg font-medium text-gray-900">Confirm Billing Adjustment</h3>
                <p className="mt-2 text-sm text-gray-600">
                  Please review the details before processing this adjustment.
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-700">Tenant:</span>
                  <span className="text-sm text-gray-900">{tenant.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-700">Type:</span>
                  <span className="text-sm text-gray-900">{selectedType?.label}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-700">Amount:</span>
                  <span className="text-sm font-semibold text-gray-900">
                    ${formData.amount?.toLocaleString()} {formData.currency}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-700">Reason:</span>
                  <span className="text-sm text-gray-900">{formData.reason}</span>
                </div>
                <div>
                  <span className="text-sm font-medium text-gray-700">Description:</span>
                  <p className="text-sm text-gray-900 mt-1">{formData.description}</p>
                </div>
                {formData.invoiceId && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium text-gray-700">Invoice ID:</span>
                    <span className="text-sm text-gray-900">{formData.invoiceId}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3 flex justify-between">
            <button
              onClick={onClose}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            
            <div className="flex space-x-3">
              {showConfirmation && (
                <button
                  onClick={() => setShowConfirmation(false)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Back
                </button>
              )}
              
              <button
                onClick={showConfirmation ? handleSubmit : () => setShowConfirmation(true)}
                disabled={!validateForm() || isLoading}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : showConfirmation ? (
                  'Confirm & Process'
                ) : (
                  'Review Adjustment'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingAdjustmentForm;